import type { LaneTotals } from "@cmmd-forge/shared/usageMerge";
import { formatCount } from "@cmmd-forge/shared/usageFormat";

export interface UsageLaneRow {
  readonly key: "pending" | "forge-agents" | "local";
  readonly label: string;
  readonly detail: string;
  readonly totals: LaneTotals;
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/**
 * The lane rows worth drawing. A lane with no records says nothing: a page
 * where everything is acknowledged, no Forge agent ran outside a Recorded
 * turn, and no CLI work exists has no rows here.
 */
export function usageLaneRows(
  pendingSync: LaneTotals,
  localOnly: LaneTotals,
  forgeAgents: LaneTotals = { costUsd: 0, totalTokens: 0, records: 0 },
): UsageLaneRow[] {
  const rows: UsageLaneRow[] = [];
  if (pendingSync.records > 0) {
    rows.push({
      key: "pending",
      label: "Pending sync",
      // Records, not turns: this is a row count, and a turn that spanned two
      // models has two of them. Only a source holding turn ids can count
      // turns, and nothing acknowledged these rows, so none did.
      detail: `${plural(pendingSync.records, "record", "records")} not yet in CMMD`,
      totals: pendingSync,
    });
  }
  if (forgeAgents.records > 0) {
    rows.push({
      key: "forge-agents",
      label: "Forge agents outside the ledger",
      detail: `${plural(forgeAgents.records, "record", "records")} from subagents Forge ran, estimated on this machine`,
      totals: forgeAgents,
    });
  }
  if (localOnly.records > 0) {
    rows.push({
      key: "local",
      label: "Local-only",
      detail: `${plural(localOnly.records, "record", "records")} from CLI sessions outside Forge`,
      totals: localOnly,
    });
  }
  return rows;
}
